/*
 * Copyright (c) 2022.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Pavel Bobylev <pavel.bobylev1988@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable} from '@angular/core';
import {CompoApiService, TemplateWithProductValues, FeatureTypes, Template, TemplateFeature, ProductTemplateFeatureValue} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {map, tap} from 'rxjs/operators';

export type TemplateFeatureValue = number | number[] | string | boolean | null;
export type FeatureInfo = {groupID: number, templateFeatureID: number, error: boolean, empty: boolean};
export type SummaryFeaturesInfo = {error: boolean, empty: number, all: number, groups: {[key: number]: {empty: number, all: number}}};
export type FeatureModel = {[key: number]: TemplateFeatureValue};

@Injectable({
  providedIn: 'root'
})
export class TemplateFeatureService {
  protected featuresModelChanged$ = new Subject<boolean>();
  protected featuresModelReloaded$ = new Subject<boolean>();
  protected productFeaturesStatus$ = new Subject<SummaryFeaturesInfo>();

  protected featureModel: FeatureModel = {};
  protected defaultFeatureModel: FeatureModel = {};
  protected changedFeatureStatuses: {[key: number]: boolean} = {};
  protected summaryFeaturesInfo: SummaryFeaturesInfo = {error: false, empty: 0, all: 0, groups: {}};
  protected features: FeatureInfo[] = [];

  constructor(protected readonly compoApiService: CompoApiService) {
  }

  public isFeaturesModelChanged(): Observable<boolean> {
    return this.featuresModelChanged$.asObservable();
  }

  public isFeaturesModelReloaded(): Observable<boolean> {
    return this.featuresModelReloaded$.asObservable();
  }

  public getProductFeaturesStatus(): Observable<SummaryFeaturesInfo> {
    return this.productFeaturesStatus$.asObservable();
  }

  public getFeatureModel(): FeatureModel {
    return this.featureModel;
  }

  public clearState(): void {
    this.features = [];
    this.featureModel = {};
    this.summaryFeaturesInfo = {error: false, empty: 0, all: 0, groups: {}};
    this.productFeaturesStatus$.next(this.summaryFeaturesInfo);
  }

  public setFeature(
    groupID: number,
    templateFeatureID: number,
    featureValue: TemplateFeatureValue | any,
    valid: boolean,
    empty: boolean,
    isStringified = false): void {

    let isModelChanged = true;
    const defaultFeatureValue = this.defaultFeatureModel[templateFeatureID];

    if (templateFeatureID in this.featureModel && defaultFeatureValue === featureValue) {
      isModelChanged = false;
    }

    if (isStringified && templateFeatureID in this.featureModel
      && typeof defaultFeatureValue === 'string'
      && typeof featureValue === 'string'
      && defaultFeatureValue.toLowerCase() === featureValue.toLowerCase()) {
      isModelChanged = false;
    }

    if (templateFeatureID in this.featureModel
      && Array.isArray(defaultFeatureValue) && Array.isArray(featureValue)
      && JSON.stringify(defaultFeatureValue.sort()) === JSON.stringify(featureValue.sort())) {
      isModelChanged = false;
    }

    this.featureModel[templateFeatureID] = featureValue;
    this.changedFeatureStatuses[templateFeatureID] = isModelChanged;

    this.featuresModelChanged$.next(this.isModelChanged());

    this.updateSummaryInfo(groupID, templateFeatureID, valid, empty);
  }

  public setDefaultFeatureValue(groupID: number,
                                templateFeatureID: number,
                                featureValue: TemplateFeatureValue,
                                valid: boolean,
                                empty: boolean): void {
    this.defaultFeatureModel[templateFeatureID] = featureValue;
    this.updateSummaryInfo(groupID, templateFeatureID, valid, empty);
  }

  protected isModelChanged(): boolean {
    for (const isChanged of Object.values(this.changedFeatureStatuses)) {
      if (isChanged) {
        return true;
      }
    }

    return false;
  }

  public loadProductTemplate(productID: number): Observable<TemplateWithProductValues> {
    return this.compoApiService.get<TemplateWithProductValues>('/template/product/' + productID)
      .pipe(
        map(response => response.data),
        tap(response => this.loadDefaultValues(response)),
      );
  }

  public bindTemplate(templateID: number, productID: number): Observable<any> {
    return this.compoApiService.post(`/template/${templateID}/bind/product/${productID}`, {})
      .pipe(map(response => response.data));
  }

  public replaceTemplate(templateID: number, productID: number): Observable<any> {
    return this.compoApiService.post(`/template/${templateID}/replace/product/${productID}`, {})
      .pipe(map(response => response.data));
  }

  public saveProductTemplateFeatures(productID: number): Observable<any> {
    const url = '/product/template-feature-product-update/' + productID;
    return this.compoApiService.post<FeatureModel, ProductTemplateFeatureValue[]>(url, this.featureModel)
      .pipe(map(response => response.data));
  }

  protected updateSummaryInfo(groupID: number, templateFeatureID: number, valid: boolean, empty: boolean): void {
    this.addOrUpdateFeatureInfo(groupID, templateFeatureID, valid, empty);

    let isError = false;
    let emptyCount = 0;
    this.summaryFeaturesInfo.groups = {};
    this.features.forEach(feature => {
      isError = feature.error ? true : isError;
      emptyCount = feature.empty ? emptyCount + 1 : emptyCount;

      if (feature.groupID in this.summaryFeaturesInfo.groups) {
        const group = this.summaryFeaturesInfo.groups[feature.groupID];
        this.summaryFeaturesInfo.groups[feature.groupID] = {empty: feature.empty ? group.empty + 1 : group.empty, all: group.all + 1};
      } else {
        this.summaryFeaturesInfo.groups[feature.groupID] = {empty: feature.empty ? 1 : 0, all: 1};
      }
    });

    this.summaryFeaturesInfo.error = isError;
    this.summaryFeaturesInfo.empty = emptyCount;
    this.summaryFeaturesInfo.all = this.features.length;
    this.productFeaturesStatus$.next(this.summaryFeaturesInfo);
  }

  protected addOrUpdateFeatureInfo(groupID: number, templateFeatureID: number, valid: boolean, empty: boolean): void {
    const index = this.features.findIndex(item => item.groupID === groupID && item.templateFeatureID === templateFeatureID);
    if (index === -1) {
      this.features.push({groupID, templateFeatureID, error: !valid, empty});
    } else {
      this.features[index] = {groupID, templateFeatureID, error: !valid, empty};
    }
  }

  protected loadDefaultValues(tmpl: TemplateWithProductValues) {
    const values = tmpl ? tmpl.featureValues : null;
    const template = tmpl ? tmpl.template : null;

    if (!template || !values || !tmpl) {
      this.featureModel = {};
      return;
    }

    this.setProductFeatureValues(template, values);
  }

  protected setProductFeatureValues(template: Template, values: ProductTemplateFeatureValue[]): void {
    this.featureModel = {};

    template.groups.forEach(groupItem => {
      groupItem.features.forEach(featureItem => {
        let featureValue: TemplateFeatureValue = null;
        if (this.isMultipleSelect(featureItem)) {
          featureValue = this.parseMultipleSelect(featureItem, values);
        } else if (this.isSelect(featureItem)) {
          featureValue = this.parseSelect(featureItem, values);
        } else if (this.isBoolean(featureItem)) {
          featureValue = this.parseBoolean(featureItem, values);
        } else if (this.isPlainFeatureValue(featureItem) || this.isComplexJsonValueType(featureItem)) {
          featureValue = this.parseString(featureItem, values);
        }

        if (featureItem.id) {
          this.featureModel[featureItem.id] = featureValue;
        }
      });
    });
  }

  public reloadProductFeatureValues(template: Template, values: ProductTemplateFeatureValue[]): void {
    this.changedFeatureStatuses = {};
    this.setProductFeatureValues(template, values);
    this.featuresModelReloaded$.next(true);
  }

  protected isMultipleSelect(feature: TemplateFeature): boolean {
    return feature.featureTypeId === FeatureTypes.SELECT && feature.multiple
      || feature.featureTypeId === FeatureTypes.CHECKBOX;
  }

  protected isSelect(feature: TemplateFeature): boolean {
    return feature.featureTypeId === FeatureTypes.SELECT && !feature.multiple
      || feature.featureTypeId === FeatureTypes.COLOR;
  }

  protected isBoolean(feature: TemplateFeature): boolean {
    return feature.featureTypeId === FeatureTypes.BOOLEAN;
  }

  protected isPlainFeatureValue(feature: TemplateFeature): boolean {
    return !this.isMultipleSelect(feature)
      && !this.isSelect(feature)
      && !this.isBoolean(feature)
      && !this.isComplexJsonValueType(feature);
  }

  protected isComplexJsonValueType(feature: TemplateFeature): boolean {
    return [FeatureTypes.SIZE, FeatureTypes.LINK, FeatureTypes.SMARTLIST].includes(feature.featureTypeId);
  }

  protected parseMultipleSelect(feature: TemplateFeature, values: ProductTemplateFeatureValue[]): TemplateFeatureValue {
    const selectValues = values
      .filter(valueItem => valueItem.templateFeatureId === feature.id)
      .map(valueItem => valueItem.id);

    return selectValues ? selectValues : null;
  }

  protected parseSelect(feature: TemplateFeature, values: ProductTemplateFeatureValue[]): TemplateFeatureValue {
    const featureValue = values.find(valueItem => valueItem.templateFeatureId === feature.id);
    return featureValue !== undefined ? featureValue.id : null;
  }

  protected parseBoolean(feature: TemplateFeature, values: ProductTemplateFeatureValue[]): TemplateFeatureValue {
    const featureValue = values.find(valueItem => valueItem.templateFeatureId === feature.id);
    return featureValue !== undefined ? JSON.parse(featureValue.value) : null;
  }

  protected parseString(feature: TemplateFeature, values: ProductTemplateFeatureValue[]): TemplateFeatureValue {
    const featureValue = values.find(valueItem => valueItem.templateFeatureId === feature.id);
    return featureValue !== undefined ? featureValue.value : null;
  }
}
